import { isMenuActive } from '@components/AppLayout/utils'
import useOnValueChange from '@hooks/useOnValueChange'
import ExpandMoreIcon from '@mui/icons-material/ExpandMoreOutlined'
import React from 'react'
import { useLocation } from 'react-router'

import * as S from './MenuItem.styles'
import type { CustomLinkComponent, SidebarMenuItem } from './types'
import { getLinkProps } from './utils'

export interface MenuItemProps {
  isMenuShrunk: boolean
  setMenuShrunk: (shrunk: boolean) => void
  menuItem: SidebarMenuItem
  linkComponent: CustomLinkComponent
}

export const MenuItem: React.FC<MenuItemProps> = ({
  menuItem,
  isMenuShrunk,
  setMenuShrunk,
  linkComponent,
}) => {
  const location = useLocation()
  const [isOpen, setIsOpen] = React.useState(false)
  const anchor = React.useRef<any>(null)

  const isActive =
    (menuItem.children &&
      menuItem.children.some((subItem) => isMenuActive(location.pathname, subItem))) ||
    isMenuActive(location.pathname, menuItem)

  useOnValueChange(isActive, () => setIsOpen(false))

  const handleClick = (event: React.MouseEvent, menuItem: SidebarMenuItem) => {
    event.stopPropagation()
    if (menuItem.children) {
      // Always open clicked menu if menu is shrunk, otherwise toggle
      setIsOpen((o) => (isMenuShrunk ? true : !o))
      // Always extend menu when has multiple items
      setMenuShrunk(false)
    } else {
      menuItem.onClick?.()
      setIsOpen(false)
    }
  }

  return (
    <>
      <S.StyledRootNavComponent
        as={menuItem.children ? 'div' : (linkComponent ?? 'div')}
        ref={anchor}
        isActive={menuItem.children && !isMenuShrunk ? false : isActive}
        isExtendable={!!menuItem.children?.length}
        onClick={(event: React.MouseEvent) => handleClick(event, menuItem)}
        {...getLinkProps(menuItem)}
      >
        <S.MenuItemBtn data-test="menu-item-label" data-test-id={menuItem.id}>
          {menuItem.icon ? (
            <S.IconSlot>
              {menuItem.icon}
              {isMenuShrunk && menuItem.badge ? <S.BadgeDot aria-hidden /> : null}
            </S.IconSlot>
          ) : null}
          <S.ExtendedLabel shouldHideLabel={isMenuShrunk}>
            <S.Label aria-label={menuItem.ariaLabel}>{menuItem.label}</S.Label>
            {menuItem.badge}
            {menuItem.children && <ExpandMoreIcon />}
          </S.ExtendedLabel>
        </S.MenuItemBtn>
      </S.StyledRootNavComponent>
      {menuItem.children && (
        <S.SubMenuListWrapper isOpen={(isOpen || isActive) && !isMenuShrunk}>
          <S.SubMenuList>
            {menuItem.children?.map((subMenuItem) => (
              <MenuItem
                isMenuShrunk={isMenuShrunk}
                menuItem={subMenuItem}
                key={subMenuItem.ariaLabel}
                linkComponent={linkComponent}
                setMenuShrunk={setMenuShrunk}
              />
            ))}
          </S.SubMenuList>
        </S.SubMenuListWrapper>
      )}
    </>
  )
}

MenuItem.displayName = 'MenuItem'
